import {computed, Ref} from 'vue';

export interface LatexFragment {
  type: 'text' | 'display' | 'inline';
  content: string;
}

/**
 * Recognises BOTH kinds of delimiters.
 *  – display :  $$ … $$   or  \[ … \]
 *  – inline  :  $  … $    or  \( … \)
 *
 * Dollar-delimited math applies Pandoc's boundary rules so prose about money
 * ("$32 is bigger than $23") is not mistaken for math: the closing delimiter may not be
 * followed by a digit, and for single dollars the opening delimiter may not be followed by
 * whitespace nor the closing delimiter preceded by it. Display math skips the whitespace
 * rules because the backend emits it padded ("$$\n…\n$$").
 * See https://pandoc.org/MANUAL.html#extension-tex_math_dollars
 *
 * A "$$" that does not open display math is consumed as text so its second dollar cannot
 * be re-read as an inline opener ("$$20-$$30"), while a closing "$" immediately followed by
 * an opening "$" still yields two consecutive fragments ("$x$$y$").
 *
 * NOTE: `app/Services/Latex/LatexFragments.php` is a PHP port of this parser — the exam printout
 * pipeline must recognise the exact same fragments as the web app. Keep the two in sync; both are
 * tested against the shared cases in `latexFragments.fixtures.json`.
 */
const mathPattern =
  /\$\$(?<dollarDisplay>[^$]+?)\$\$(?!\d)|\\\[(?<bracketDisplay>[^$]+?)\\]|\$(?![\s$])(?<dollarInline>[^$]*?[^\s$])\$(?!\d)|\\\((?<bracketInline>[^$]+?)\\\)|\$\$/g;

export function parseLatexFragments(rawValue: string): LatexFragment[] {
  // We replace all escaped dollar signs so that we can avoid matching them as part of
  // finding LaTeX fragments in the string.
  const normalized = rawValue.replace(/\\\$/g, '<dollar>');

  const result: LatexFragment[] = [];
  let cursor = 0;

  for (const match of normalized.matchAll(mathPattern)) {
    pushFragment(result, {type: 'text', content: normalized.slice(cursor, match.index)});
    pushFragment(result, toFragment(match));
    cursor = match.index + match[0].length;
  }
  pushFragment(result, {type: 'text', content: normalized.slice(cursor)});

  return result.map(({type, content}) => ({
    type,
    content: content.replace(/<dollar>/g, '\\$'),
  }));
}

function toFragment(match: RegExpExecArray): LatexFragment {
  const {dollarDisplay, bracketDisplay, dollarInline, bracketInline} = match.groups ?? {};

  if (dollarDisplay !== undefined || bracketDisplay !== undefined) {
    return {type: 'display', content: dollarDisplay ?? bracketDisplay};
  }
  if (dollarInline !== undefined || bracketInline !== undefined) {
    return {type: 'inline', content: dollarInline ?? bracketInline};
  }

  return {type: 'text', content: match[0]};
}

function pushFragment(result: LatexFragment[], fragment: LatexFragment): void {
  if (!fragment.content) {
    return;
  }

  const last = result.at(-1);
  if (fragment.type === 'text' && last?.type === 'text') {
    last.content += fragment.content;
    return;
  }

  result.push(fragment);
}

export function latexToPlainText(raw: string | null | undefined): string {
  if (!raw) {
    return '';
  }

  return parseLatexFragments(raw)
    .map((fragment) => fragment.content)
    .join('')
    .trim();
}

export function useLatexFragments(raw: Ref<string | null>) {
  const fragments = computed<LatexFragment[]>(() => {
    if (!raw.value) {
      return [];
    }

    return parseLatexFragments(raw.value);
  });

  return {
    fragments,
  };
}
